import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { AtiraEmpty } from "../../../../../components/AtiraEmpty";
import { Drawer } from "../../../../../components/Drawer";
import { Flex } from "../../../../../components/Flex";
import { Info } from "../../../../../components/Info";
import { Spinner } from "../../../../../components/Spinner";
import { TaskPriority } from "../../../../../model/task/types/TaskPriority.enum";
import { integrationSliceSelectors } from "../../../../../redux/integration/integration.selector";
import { integrationActions } from "../../../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../../../redux/store";
import { userSliceSelectors } from "../../../../../redux/user/user.selector";
import { AtiraToast } from "../../../../../utils/AtiraToast";
import { Radio } from "../../../../../utils/local-antd";
import { WACommentCard } from "./WACommentCard";
import { WACommentForm } from "./WACommentForm";

type Filter = "open" | "resolved" | "all";

type Props = {
  open: boolean;
  onClose: () => void;
  conversationId: string;
  title?: string;
};

export const WACommentsDrawer: React.FC<Props> = ({
  open,
  onClose,
  conversationId,
  title,
}) => {
  const [filter, setFilter] = useState<Filter>("open");
  const [creating, setCreating] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const integration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );
  const comments = useAppSelector((state) =>
    integrationSliceSelectors.selectWhatsAppMetaComments(state, conversationId),
  );

  const visible = (comments || []).filter((comment) => {
    const resolved = Boolean(comment.resolvedAt);

    if (filter === "open") {
      return !resolved;
    }

    if (filter === "resolved") {
      return resolved;
    }

    return true;
  });

  const onCreate = async (body: string, priority: TaskPriority) => {
    try {
      setCreating(true);

      await dispatch(
        integrationActions.createWhatsappMetaComment({
          userId,
          integrationId: integration!._id,
          conversationId,
          body,
          priority,
        }),
      ).unwrap();

      if (filter === "resolved") {
        setFilter("open");
      }

      return true;
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);

      return false;
    } finally {
      setCreating(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="large"
      title={title || t("integration.whatsapp.meta.comments.title")}
    >
      <Flex flexDirection="column" gap="m" width="100%">
        <Info info={t("integration.whatsapp.meta.comments.info")} />

        <WACommentForm
          submitTitle={t("common.add")}
          loading={creating}
          onSubmit={onCreate}
        />

        <Radio.Group
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          optionType="button"
          options={[
            {
              value: "open",
              label: t("integration.whatsapp.meta.comments.filter.open"),
            },
            {
              value: "resolved",
              label: t("integration.whatsapp.meta.comments.filter.resolved"),
            },
            {
              value: "all",
              label: t("integration.whatsapp.meta.comments.filter.all"),
            },
          ]}
        />

        {!comments ? (
          <Flex justifyContent="center" padding="l">
            <Spinner color="main" size="1.5rem" />
          </Flex>
        ) : visible.length ? (
          visible.map((comment) => (
            <WACommentCard key={comment._id} comment={comment} />
          ))
        ) : (
          <AtiraEmpty
            description={
              filter === "open" && comments.length
                ? t("integration.whatsapp.meta.comments.emptyOpen")
                : t("integration.whatsapp.meta.comments.empty")
            }
          />
        )}
      </Flex>
    </Drawer>
  );
};
